import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from '../store';

// ──────────────────────────────────────────────────────────────────────────────
// launchpad slice
//
// Holds the result of GET /api/v1/practices/{id}/launchpad-summary/
// (T17 BE endpoint). One cheap call on Launchpad mount that provides:
//   triage   — 5 action-card counts (overdue / due_today / attended_no_outcome
//               / todays_consults / fta_to_rebook)
//   new_leads_by_source_today — [{source, count}] for the worklist header
//   money    — pipeline_value / converted_this_month / converted_last_month /
//               treatment_income_today
//
// While T17 is in progress the FE scaffolds against this shape; once the
// endpoint ships, wiring getLaunchpadSummary into LaunchpadView.jsx activates
// all four zones with live data.
// ──────────────────────────────────────────────────────────────────────────────

const initialState = {
    summary: null,      // null = not yet loaded
    loading: false,
    error: null,
};

const slice = createSlice({
    name: 'launchpad',
    initialState,
    reducers: {
        startLoading(state) {
            state.loading = true;
            state.error = null;
        },
        setSummary(state, action) {
            state.summary = action.payload;
            state.loading = false;
        },
        setError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },
        clearSummary(state) {
            state.summary = null;
            state.loading = false;
            state.error = null;
        },
    },
});

export default slice.reducer;
export const { startLoading, setSummary, setError, clearSummary } = slice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectLaunchpadSummary = (state) => state.launchpad?.summary ?? null;
export const selectLaunchpadLoading = (state) => state.launchpad?.loading ?? false;
// Non-null when the summary call failed (403 / throttle / network).
// Consumers degrade gracefully — show "—" for triage/money, never white-screen.
export const selectLaunchpadError = (state) => state.launchpad?.error ?? null;

// ── Thunk ─────────────────────────────────────────────────────────────────────

/**
 * Fetch the Launchpad summary (T17 endpoint).
 * Caps Launchpad mount to ≤2 API calls (summary + first worklist tab).
 *
 * @param {number|string} practiceId
 */
export function getLaunchpadSummary(practiceId) {
    return async () => {
        if (!practiceId) return;
        dispatch(startLoading());
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/launchpad-summary/`,
            );
            dispatch(setSummary(res.data));
        } catch {
            dispatch(setError('Failed to load Launchpad summary'));
        }
    };
}
